import { Fragment } from "react";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/kit";
import type { CustomerCohortsResultDTO } from "@/modules/reports/domain/types";

type CustomerCohortChartProps = {
  result?: CustomerCohortsResultDTO;
  isLoading?: boolean;
};

function retentionColor(percent: number) {
  if (percent >= 75) return "bg-secondary/80 text-secondary-foreground";
  if (percent >= 50) return "bg-secondary/50 text-foreground";
  if (percent >= 25) return "bg-secondary/25 text-foreground";
  if (percent > 0) return "bg-secondary/10 text-muted-foreground";
  return "bg-muted/60 text-muted-foreground";
}

export function CustomerCohortChart({
  result,
  isLoading,
}: CustomerCohortChartProps) {
  if (isLoading) {
    return <Skeleton className="h-[320px] w-full rounded-2xl" />;
  }

  const rows = result?.data ?? [];
  const maxOffset = result?.maxOffset ?? 6;

  if (rows.length === 0) {
    return (
      <div className="brand-card flex h-[280px] items-center justify-center p-6 text-muted-foreground text-sm">
        ຍັງບໍ່ມີຂໍ້ມູນ cohort — ຕ້ອງມີອໍເດີ້ຢ່າງໜ້ອຍ 2 ເດືອນ
      </div>
    );
  }

  const offsets = Array.from({ length: maxOffset + 1 }, (_, index) => index);

  return (
    <div className="brand-card min-w-0 overflow-hidden p-4 sm:p-5">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-2">
        <div>
          <h2 className="font-display text-lg">Cohort retention</h2>
          <p className="text-muted-foreground text-xs">
            % ລູກຄ້າທີ່ກັບມາສັ່ງ — ຈາກ first confirmed
          </p>
        </div>
        <div className="flex items-center gap-1 text-muted-foreground text-xs">
          <span className="size-3 rounded-sm bg-muted/60" />
          <span>0%</span>
          <span className="size-3 rounded-sm bg-secondary/25" />
          <span>25%</span>
          <span className="size-3 rounded-sm bg-secondary/50" />
          <span>50%</span>
          <span className="size-3 rounded-sm bg-secondary/80" />
          <span>75%+</span>
        </div>
      </div>

      <div className="overflow-x-auto">
        <div
          className="grid min-w-[480px] gap-1"
          style={{
            gridTemplateColumns: `88px repeat(${offsets.length}, minmax(44px, 1fr))`,
          }}
        >
          <div className="text-muted-foreground text-xs" />
          {offsets.map((offset) => (
            <div
              key={`head-${offset}`}
              className="text-center text-muted-foreground text-xs"
            >
              +{offset}
            </div>
          ))}

          {rows.map((row) => (
            <Fragment key={row.cohortMonth}>
              <div className="flex items-center pr-2 font-medium text-xs">
                {row.cohortMonth}
                <span className="ml-1 text-muted-foreground">
                  ({row.cohortSize})
                </span>
              </div>
              {offsets.map((offset) => {
                const percent = row.retentionByMonth[offset] ?? 0;
                const retained = row.retainedCounts[offset] ?? 0;
                return (
                  <div
                    key={`${row.cohortMonth}-${offset}`}
                    title={`ກຸ່ມ ${row.cohortMonth} · ເດືອນ+${offset}: ${percent}% (${retained}/${row.cohortSize} ຄົນ)`}
                    className={cn(
                      "flex min-h-9 items-center justify-center rounded-md text-xs tabular-nums",
                      retentionColor(percent),
                    )}
                  >
                    {percent > 0 ? `${percent}%` : "—"}
                  </div>
                );
              })}
            </Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}
